import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";
import { ConfirmationDialog } from "../common/components/functional/confirmationDialog/ConfirmationDialog";
import { ReactComponent as ContinueIcon } from "../common/icons/arrows-right-filled.svg";

const UnsavedChangesContext = createContext({
  openDialog: () => {},
});

type Props = {
  children: React.ReactNode;
  isDirty: boolean;
  onSuccessNavigate?: () => void;
  isSubmitted?: boolean;
};

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

export const UnsavedChangesProvider = ({
  children,
  isDirty,
  onSuccessNavigate,
  isSubmitted,
}: Props) => {
  const [showDialog, setShowDialog] = useState(false);
  const { t } = useTranslation("common");

  useEffect(() => {
    const confirmBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    if (isDirty) {
      // confirm browser's refresh, show native confirmation dialog
      window.addEventListener("beforeunload", confirmBeforeUnload);
    }

    return () =>
      window.removeEventListener("beforeunload", confirmBeforeUnload);
  }, [isDirty]);

  let blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (isSubmitted && onSuccessNavigate) {
      // ensure that the blocker state is updated before navigating
      setTimeout(() => onSuccessNavigate(), 100);
      if (blocker.state === "blocked") blocker.reset();
    }
  }, [isSubmitted, onSuccessNavigate, blocker, isDirty]);

  const openDialog = () => setShowDialog(true);
  const onCancel = () => setShowDialog(false);

  useEffect(() => {
    if (blocker.state === "blocked" && isDirty) openDialog();
  }, [isDirty, blocker]);

  const onConfirm = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
      onCancel();
    }
  };

  return (
    <UnsavedChangesContext.Provider value={{ openDialog }}>
      {children}
      {showDialog && (
        <ConfirmationDialog
          isOpen={showDialog}
          title={t("unsavedChangesTitle")}
          contentText={t("unsavedChangesContextText")}
          confirmText={t("continueWithoutSaving")}
          cancelText={t("back")}
          onConfirm={onConfirm}
          onCancel={onCancel}
          sx={{ backgroundColor: "#fff", maxWidth: "444px" }}
          CancelButtonProps={{ startIcon: <ArrowBackIcon /> }}
          ConfirmButtonProps={{ endIcon: <ContinueIcon /> }}
        />
      )}
    </UnsavedChangesContext.Provider>
  );
};
